import { appendAppLog } from '../platform/appLog';
import { istBrowser } from '../platform/gleis';
import { browserHatFreigabe, ziehAbzug, type ShotResult } from '../platform/screenshot';
import { relaySender } from './anhaenge';
import type { ServerClient } from './client';
import type { MultipartSender } from './multipart';
import type { Ausgang } from './protokollArchiv';

/**
 * Relays `SCREENSHOT {screenshotID}`: ein Bild der App an
 * `POST /rest/v1/client-screenshot/upload?screenshotID=…`, Teil `datei`
 * (`RELAY/docs/protokoll/PROTOKOLL-CLIENT-SERVER.md` §2.2).
 *
 * Das Bild wird **nicht** auf dem Gerät abgelegt und nicht ins Archiv übernommen. Es ist nur
 * fünf Minuten gefragt: kein Nachholen, keine Warteschlange — ein liegengebliebener Befehl kommt
 * im nächsten Abgleich wieder, solange Relay ihn noch liefert.
 *
 * **Ausgang:** `'fertig'` heisst quittieren — geliefert, aufgegeben (`404`/`410`) oder über
 * `client-screenshot/failed` gemeldet. `'spaeter'` heisst liegen lassen: kein Netz, `401`, oder
 * der erste `5xx` („innerhalb der Frist einmal später wiederholen“).
 */
export const SCREENSHOT = 'SCREENSHOT';
export const FOTO_PFAD = '/rest/v1/client-screenshot/upload';
export const FOTO_FEHLER_PFAD = '/rest/v1/client-screenshot/failed';
/** Für den zweiten Versuch nach `413`: dieselbe Grösse, stärker gepackt. */
const KLEINER = 0.6;

type Versuch =
  | { fehlschlag: string }
  | { antwort: Awaited<ReturnType<MultipartSender>>; bytes: number };

/** Welche Abzüge schon einmal vertagt wurden — nur im Speicher, die Frist ist kurz. */
const vertagt = new Set<string>();

export interface FotoDeps {
  client: ServerClient;
  basisUrl: string;
  screenshotID: string;
  log?: (text: string, stufe?: 'warn') => Promise<void>;
  /** Kurz das Symbol zeigen — erst nach der Aufnahme, damit es nicht im Bild steht. */
  aufgenommen?: () => void;
  /** Einschiebbar für Tests: die Aufnahme; wirft mit einem Grund auf Englisch. */
  nimm?: (qualitaet?: number) => Promise<ShotResult>;
  /** Einschiebbar für Tests: der Transport zum Token. */
  sender?: (token: string) => MultipartSender;
}

async function nimmAuf(qualitaet?: number): Promise<ShotResult> {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    throw new Error('app not in foreground');
  }
  // Entscheid des Nutzers (2026-10-22): im Browser nie nach einer Freigabe fragen.
  if (istBrowser() && !browserHatFreigabe()) throw new Error('screenshot not supported in browser');
  return ziehAbzug(qualitaet === undefined ? {} : { qualitaet });
}

export async function ladeBildschirmfotoHoch(deps: FotoDeps): Promise<Ausgang> {
  const log = deps.log ?? appendAppLog;
  const id = deps.screenshotID;
  const was = 'Relay: SCREENSHOT ' + id;

  const meldeFehlschlag = async (error: string): Promise<Ausgang> => {
    vertagt.delete(id);
    try {
      await deps.client.post(FOTO_FEHLER_PFAD, { screenshotID: id, error: error.slice(0, 200) });
    } catch (fehler) {
      await log(was + ': Fehlschlag nicht meldbar, später erneut — ' + String(fehler), 'warn');
      return 'spaeter';
    }
    await log(was + ' gescheitert, an Relay gemeldet: ' + error, 'warn');
    return 'fertig';
  };
  const einmalSpaeter = async (grund: string): Promise<Ausgang> => {
    if (vertagt.has(id)) return meldeFehlschlag(grund);
    vertagt.add(id);
    await log(was + ': ' + grund + ', später einmal erneut.', 'warn');
    return 'spaeter';
  };

  const sende = async (qualitaet?: number): Promise<Versuch> => {
    let bild: ShotResult;
    try {
      bild = await (deps.nimm ?? nimmAuf)(qualitaet);
    } catch (fehler) {
      return { fehlschlag: fehler instanceof Error ? fehler.message : String(fehler) };
    }
    deps.aufgenommen?.();
    const { token } = await deps.client.anmelden();
    const antwort = await (deps.sender ?? relaySender)(token)({
      url: deps.basisUrl.replace(/\/+$/, '') + FOTO_PFAD + '?screenshotID=' + encodeURIComponent(id),
      felder: {},
      datei: {
        name: 'screenshot.' + bild.format,
        mime: bild.format === 'png' ? 'image/png' : 'image/jpeg',
        base64: bild.base64,
        feld: 'datei',
      },
    });
    return { antwort, bytes: Math.round((bild.base64.length * 3) / 4) };
  };

  try {
    let r = await sende();
    if ('antwort' in r && r.antwort.status === 413) r = await sende(KLEINER);
    if ('fehlschlag' in r) return meldeFehlschlag(r.fehlschlag);
    const { antwort } = r;
    if (antwort.ok) {
      vertagt.delete(id);
      await log(was + ': ' + String(r.bytes) + ' Bytes übertragen.');
      return 'fertig';
    }
    if (antwort.status === 401) {
      // Abgelaufenes Token: neu anmelden und im nächsten Takt wieder versuchen.
      deps.client.vergessen();
      return 'spaeter';
    }
    if (antwort.status === 404 || antwort.status === 410) {
      // Unbekannt oder abgelaufen: aufgeben, nicht wiederholen, nichts melden.
      vertagt.delete(id);
      await log(was + ': HTTP ' + String(antwort.status) + ', aufgegeben.', 'warn');
      return 'fertig';
    }
    if (antwort.status >= 400 && antwort.status < 500) {
      return meldeFehlschlag('HTTP ' + String(antwort.status) + ' ' + antwort.text.slice(0, 150));
    }
    return einmalSpaeter('HTTP ' + String(antwort.status));
  } catch (fehler) {
    return einmalSpaeter('nicht übertragen — ' + String(fehler));
  }
}
